import React, { useRef, useState, useEffect } from 'react'
import dynamic from 'next/dynamic'
import Slider from 'react-slick'
import styles from './SuggestedSet.module.scss'
import { ChevronFatRight } from '@components/icons'
import { useProductUI } from '@pylot-data/hooks/product/use-product-ui'
import { SidebarSuggestedSet } from '@corratech/pylot-ui'
import { useTranslation } from 'next-i18next/pages'
import { ProductPrice } from '@components/common/ProductPrice/ProductPrice'
import { QuantitySelector } from '@components/QuantitySelector/QuantitySelector'
import { Button } from '@corratech/pylot-ui'
import type { MediaGalleryInterface } from '@pylot-data/pylotschema'
import { useIsMobileScreen } from '@lib/utils/mediaQuery'
import { useAddToCart } from '@pylot-data/hooks/cartManager/use-add-to-cart'
import getStockValidation from '@pylot-data/api/operations/get-stock-validation'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import { RichContent } from '@corratech/pylot-rich-content'
import { cartUpdateEvent } from '@lib/utils/exponeaTrackingEvents'
import { Cross } from '@components/icons'
import { formatCurrency } from '@lib/utils/formatCurrency'
import { LoadingIndicator } from '@components/LoadingIndicator'

import cn from 'classnames'

const ModalLoading = () => <LoadingIndicator />
ModalLoading.displayName = 'ModalLoading'

const Modal = dynamic(() => import('@corratech/pylot-ui/src/Modal'), {
    loading: ModalLoading
})

interface ProductImage {
    url: string
}

interface Money {
    value: number
    currency: string
}

interface PriceRange {
    minimum_price: ProductPrice
    maximum_price: ProductPrice
}

export interface Product {
    id: number
    name: string
    url_key: string
    small_image: ProductImage
    price_range: any
    sku: string
    media_gallery: MediaGalleryInterface[]
    description: { html: '' }
    short_description: { html: '' }
    medidas_y_materialidad: string
    ppum: string
    is_set: number
}

export interface MainProductCart {
    sku: string
    uid: string
    parent_sku?: string
}

interface SuggestedSetProps {
    products?: Product[]
    mainProduct?: Product
    mainProductCart?: MainProductCart
}

const STORAGE_KEY_PREFIX = 'suggestedSet_'

function getSetStorageKey(products: Product[]) {
    const ids = products.map((p) => p.id).sort((a, b) => a - b)
    return `${STORAGE_KEY_PREFIX}${ids.join('-')}`
}

function SuggestedSet({
    products: relatedProducts = [],
    mainProduct,
    mainProductCart
}: SuggestedSetProps) {
    const { t } = useTranslation('pdp')
    const infoMessage = t(
        'Review your cart, we adjusted the available units for one of your products'
    )
    const safeRelated = Array.isArray(relatedProducts) ? relatedProducts : []
    const displayList: Product[] =
        mainProduct != null ? [...safeRelated, mainProduct] : safeRelated
    const sliderRef = useRef<Slider | null>(null)
    const lastLoadedKeyRef = useRef<string | null>(null)
    const [isAdding, setIsAdding] = useState(false)
    const [currentSlide, setCurrentSlide] = useState(0)
    const [isSidebarOpen, setSidebarOpen] = useState(false)
    const [isSetListModalOpen, setSetListModalOpen] = useState(false)
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
    const [productQuantities, setProductQuantities] = useState<
        Record<number, number>
    >({})
    const {
        openSidebar,
        openCartToast,
        openToast,
        closeSidebar,
        LoadingIndicator
    } = useUI()
    const {
        setQuantity,
        quantity,
        variant,
        selectedOptionUIDs,
        isButtonDisabled,
        buttonLabel: buttonLabelWithoutAdding
    } = useProductUI(selectedProduct)
    const isMobile = useIsMobileScreen()
    const { addToCart } = useAddToCart()
    const slidesToShow = 3
    const TIMEOUT_AFTER_OPENING_SIDEBAR = 50
    const buttonLabel = isAdding
        ? t('Adding...')
        : buttonLabelWithoutAdding === 'Add to cart'
          ? t('Add to cart')
          : buttonLabelWithoutAdding

    const setKey = displayList?.length > 0 ? getSetStorageKey(displayList) : ''

    // Cargar cantidades guardadas o por defecto todos en 1 (solo al montar o al cambiar de set)
    useEffect(() => {
        if (!displayList.length) return
        const key = getSetStorageKey(displayList)
        const isNewSet = lastLoadedKeyRef.current !== key
        const needsHydration = Object.keys(productQuantities).length === 0
        if (!isNewSet && !needsHydration) return

        lastLoadedKeyRef.current = key
        try {
            const raw =
                typeof window !== 'undefined' &&
                window.sessionStorage.getItem(key)
            const parsed = raw
                ? (JSON.parse(raw) as Record<string, number>)
                : null
            const isValid =
                parsed &&
                typeof parsed === 'object' &&
                displayList.every(
                    (_, i) =>
                        typeof parsed[String(i)] === 'number' &&
                        parsed[String(i)] >= 0
                )
            if (isValid) {
                const normalized: Record<number, number> = {}
                displayList.forEach((_, i) => {
                    normalized[i] = Math.min(
                        99,
                        Math.max(0, parsed![String(i)] ?? 1)
                    )
                })
                setProductQuantities(normalized)
                return
            }
        } catch {
            // ignore invalid stored data
        }
        setProductQuantities(
            Object.fromEntries(displayList.map((_, index) => [index, 1]))
        )
    }, [displayList.length, setKey])

    // Persistir cantidades cuando el usuario las modifica
    useEffect(() => {
        if (!displayList.length || Object.keys(productQuantities).length === 0)
            return
        if (lastLoadedKeyRef.current !== setKey) return
        try {
            window.sessionStorage.setItem(
                setKey,
                JSON.stringify(productQuantities)
            )
        } catch {
            // ignore storage errors
        }
    }, [productQuantities, setKey, displayList.length])

    if (!displayList.length) return null

    if (mainProduct != null && displayList.length === 1) return null

    const shouldUseSlider = displayList.length > 3
    const settings = {
        dots: false,
        infinite: false,
        speed: 300,
        slidesToShow: 3,
        slidesToScroll: 1,
        arrows: false,
        beforeChange: (_: number, next: number) => setCurrentSlide(next)
    }

    const isFirstSlide = currentSlide === 0
    const isLastSlide = currentSlide >= displayList.length - slidesToShow

    const handlePrev = () => {
        if (!isFirstSlide) sliderRef.current?.slickPrev()
    }

    const handleNext = () => {
        if (!isLastSlide) sliderRef.current?.slickNext()
    }

    const handleOpenSidebar = (product: Product) => {
        setSelectedProduct(product)
        setSidebarOpen(true)
    }

    const handleCloseSidebar = () => {
        setSidebarOpen(false)
        setSelectedProduct(null)
    }

    const getProductQty = (index: number) => productQuantities[index] ?? 0
    const setProductQty = (
        index: number,
        valueOrUpdater: number | ((prev: number) => number)
    ) => {
        setProductQuantities((prev) => {
            const current = prev[index] ?? 0
            const next =
                typeof valueOrUpdater === 'function'
                    ? valueOrUpdater(current)
                    : valueOrUpdater
            const clamped = Math.min(99, Math.max(0, next))
            return { ...prev, [index]: clamped }
        })
    }

    const mainProductIndex = mainProduct != null ? displayList.length - 1 : -1

    const addAllToCartHandler = async () => {
        const hasAnySelection = displayList.some((_, i) => getProductQty(i) > 0)
        const selectedList = hasAnySelection
            ? displayList
                  .map((p, index) => ({ product: p, index }))
                  .filter(({ index }) => getProductQty(index) > 0)
            : displayList.map((p, index) => ({ product: p, index }))
        const cartItems = selectedList.map(({ product: p, index: i }) => {
            const qty = hasAnySelection ? getProductQty(i) : 1
            const effectiveQty = qty > 0 ? qty : 1
            if (
                mainProductCart != null &&
                mainProductIndex >= 0 &&
                i === mainProductIndex
            ) {
                return {
                    sku: mainProductCart.sku,
                    uid: mainProductCart.uid,
                    ...(mainProductCart.parent_sku && {
                        parent_sku: mainProductCart.parent_sku
                    }),
                    quantity: effectiveQty
                }
            }
            return {
                sku: p.sku,
                uid: String(p.id),
                quantity: effectiveQty
            }
        })
        const selectedProductsList = selectedList.map(({ product }) => product)
        const response = await addToCart(cartItems)
        const errorMessage =
            response?.errors?.[0]?.message ??
            response?.user_errors?.[0]?.message ??
            null
        const successMessage =
            selectedProductsList.length === 1
                ? t('{{name}} was added to cart', {
                      name: selectedProductsList[0].name
                  })
                : t('Add {{ amount }} products into shopping cart', {
                      amount: selectedProductsList.length
                  })
        if (!isMobile) {
            openSidebar()
            setTimeout(() => closeSidebar(), 4000)
        }
        if (response?.data && !errorMessage) {
            const { cart } = response.data
            selectedList.forEach(({ product: p, index: i }) => {
                const sku =
                    mainProductCart != null && i === mainProductIndex
                        ? mainProductCart.sku
                        : p.sku
                const uid =
                    mainProductCart != null && i === mainProductIndex
                        ? mainProductCart.uid
                        : String(p.id)
                cartUpdateEvent('add', { ...p, sku, uid }, cart)
            })
        }
        setTimeout(
            () =>
                isMobile
                    ? openToast(
                          errorMessage ? errorMessage : successMessage,
                          errorMessage ? ToastType.Warning : ToastType.Success,
                          true
                      )
                    : openCartToast(
                          errorMessage ? errorMessage : successMessage,
                          errorMessage ? ToastType.Warning : ToastType.Success
                      ),
            TIMEOUT_AFTER_OPENING_SIDEBAR
        )
    }

    const addProductHandler = async () => {
        if (!selectedProduct || !variant) return

        try {
            setIsAdding(true)
            const stockValidationSku = [
                {
                    sku: selectedProduct.sku,
                    quantity: quantity.toString()
                }
            ]
            const { getValidationStock: beforePlaceOrderStockValidation } =
                await getStockValidation({
                    input: {
                        items: stockValidationSku
                    }
                })

            const totalStock = beforePlaceOrderStockValidation[0].stock

            const stock = Number(totalStock)

            const validQuantity = quantity > stock

            const addToCartOptions = selectedOptionUIDs

            // 'variant' of a SimpleProduct is the same as 'product'
            const response = await addToCart([
                {
                    sku: variant.sku,
                    uid: variant.uid,
                    ...(variant.sku !== selectedProduct.sku && {
                        parent_sku: selectedProduct.sku
                    }),
                    selected_options: addToCartOptions,
                    quantity: validQuantity ? stock : quantity
                }
            ])

            const errorMessage =
                response?.errors?.[0]?.message ??
                response?.user_errors?.[0]?.message ??
                null
            const successMessage = t('{{name}} was added to cart', {
                name: selectedProduct.name
            })

            if (!isMobile) {
                openSidebar()
                setTimeout(() => {
                    closeSidebar()
                }, 4000)
            }

            if (response?.data && !errorMessage) {
                const { cart } = response.data
                const addedProduct = {
                    ...selectedProduct,
                    sku: variant.sku,
                    uid: variant.uid
                }
                cartUpdateEvent('add', addedProduct, cart)
            }
            handleCloseSidebar()
            setTimeout(
                () =>
                    isMobile
                        ? openToast(
                              validQuantity
                                  ? infoMessage
                                  : errorMessage
                                    ? errorMessage
                                    : successMessage,
                              validQuantity
                                  ? ToastType.Warning
                                  : errorMessage
                                    ? ToastType.Warning
                                    : ToastType.Success,
                              true
                          )
                        : openCartToast(
                              validQuantity
                                  ? infoMessage
                                  : errorMessage
                                    ? errorMessage
                                    : successMessage,
                              validQuantity
                                  ? ToastType.Warning
                                  : errorMessage
                                    ? ToastType.Warning
                                    : ToastType.Success
                          ),
                TIMEOUT_AFTER_OPENING_SIDEBAR
            )
            setQuantity(1)
        } catch (error) {
            throw new Error(t('Something went wrong while adding the product'))
        } finally {
            setIsAdding(false)
        }
    }
    return (
        <div className={styles.suggestedSetWrapper}>
            <div className={styles.setContainer}>
                <span className={styles.title}>{t('Complete the set')}</span>
                {shouldUseSlider && (
                    <div className={styles.arrows}>
                        <div
                            className={`${styles.leftArrowSlickSuggSet} ${
                                !isFirstSlide ? styles.active : ''
                            }`}
                            onClick={handlePrev}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) =>
                                (e.key === 'Enter' || e.key === ' ') &&
                                handlePrev()
                            }
                        >
                            <ChevronFatRight />
                        </div>
                        <div
                            className={`${styles.rightArrowSlickSuggSet} ${
                                !isLastSlide ? styles.active : ''
                            }`}
                            onClick={handleNext}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) =>
                                (e.key === 'Enter' || e.key === ' ') &&
                                handleNext()
                            }
                        >
                            <ChevronFatRight />
                        </div>
                    </div>
                )}
            </div>

            {shouldUseSlider ? (
                <Slider ref={sliderRef} {...settings}>
                    {displayList.map((product, index) => (
                        <div
                            className={styles.productCardSlider}
                            key={`${product.id}-${index}`}
                            data-sku={product.sku}
                        >
                            <div
                                className={styles.productButton}
                                onClick={() => {
                                    setSelectedProduct(product)
                                    handleOpenSidebar(product)
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        setSelectedProduct(product)
                                        handleOpenSidebar(product)
                                    }
                                }}
                            >
                                <div className={styles.productImageWrapper}>
                                    <input
                                        type="checkbox"
                                        className={styles.productCheckbox}
                                        checked={getProductQty(index) > 0}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                            e.stopPropagation()
                                            setProductQty(index, (q) =>
                                                q > 0 ? 0 : 1
                                            )
                                        }}
                                        aria-label={t('Select product')}
                                    />
                                    <div
                                        className={styles.productImage}
                                        style={{
                                            backgroundImage: `url(${product.small_image.url})`
                                        }}
                                    />
                                    {!isMobile && (
                                        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, i18next/no-literal-string
                                        <div
                                            className={styles.cardQtySelector}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) =>
                                                e.stopPropagation()
                                            }
                                            role="group"
                                            aria-label={t('pdp||QTY:')}
                                        >
                                            <QuantitySelector
                                                size="compact" // eslint-disable-line i18next/no-literal-string
                                                min={0}
                                                quantity={getProductQty(index)}
                                                setQuantity={(updater) =>
                                                    setProductQty(
                                                        index,
                                                        updater
                                                    )
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className={styles.productDetail}>
                                    <span className={styles.productName}>
                                        {product.name}
                                    </span>
                                    <span className={styles.productPrice}>
                                        <ProductPrice
                                            className="suggestedSetPrice"
                                            priceRange={product.price_range}
                                        />
                                    </span>
                                    {product.is_set === 1 &&
                                    typeof product.ppum === 'number' &&
                                    !isNaN(product.ppum) ? (
                                        <div
                                            className={styles.pricePerUnitCont}
                                        >
                                            <span>
                                                {t(
                                                    'common||pdp||Price per unit'
                                                )}{' '}
                                                {formatCurrency(
                                                    product.ppum
                                                        ? product.ppum
                                                        : ''
                                                )}
                                            </span>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    ))}
                </Slider>
            ) : (
                <div className={styles.productList}>
                    {displayList.map((product, index) => (
                        <div
                            className={styles.productCard}
                            key={`${product.id}-${index}`}
                            data-sku={product.sku}
                        >
                            <div
                                className={styles.productButton}
                                onClick={() => {
                                    setSelectedProduct(product)
                                    handleOpenSidebar(product)
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        setSelectedProduct(product)
                                        handleOpenSidebar(product)
                                    }
                                }}
                            >
                                <div className={styles.productImageWrapper}>
                                    <input
                                        type="checkbox"
                                        className={styles.productCheckbox}
                                        checked={getProductQty(index) > 0}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                            e.stopPropagation()
                                            setProductQty(index, (q) =>
                                                q > 0 ? 0 : 1
                                            )
                                        }}
                                        aria-label={t('Select product')}
                                    />
                                    <div
                                        className={styles.productImage}
                                        style={{
                                            backgroundImage: `url(${product.small_image.url})`
                                        }}
                                    />
                                    {!isMobile && (
                                        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, i18next/no-literal-string
                                        <div
                                            className={styles.cardQtySelector}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) =>
                                                e.stopPropagation()
                                            }
                                            role="group"
                                            aria-label={t('pdp||QTY:')}
                                        >
                                            <QuantitySelector
                                                size="compact" // eslint-disable-line i18next/no-literal-string
                                                min={0}
                                                quantity={getProductQty(index)}
                                                setQuantity={(updater) =>
                                                    setProductQty(
                                                        index,
                                                        updater
                                                    )
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className={styles.productDetail}>
                                    <span className={styles.productName}>
                                        {product.name}
                                    </span>
                                    <span className={styles.productPrice}>
                                        <ProductPrice
                                            className="suggestedSetPrice"
                                            priceRange={product.price_range}
                                        />
                                    </span>
                                    {product.is_set === 1 &&
                                    typeof product.ppum === 'number' &&
                                    !isNaN(product.ppum) ? (
                                        <div
                                            className={styles.pricePerUnitCont}
                                        >
                                            <span>
                                                {t(
                                                    'common||pdp||Price per unit'
                                                )}{' '}
                                                {formatCurrency(
                                                    product.ppum
                                                        ? product.ppum
                                                        : ''
                                                )}
                                            </span>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className={styles.llevarSetContainer}>
                <Button
                    type="button"
                    className={styles.llevarSetButton}
                    onClick={() => {
                        if (isMobile) {
                            setSetListModalOpen(true)
                        } else {
                            addAllToCartHandler()
                        }
                    }}
                    disabled={isAdding}
                >
                    {t('LLEVAR SET')}
                </Button>
            </div>

            {isMobile && (
                <Modal
                    open={isSetListModalOpen}
                    onClose={() => setSetListModalOpen(false)}
                    className={styles.setListModal}
                    allowScrolling
                    closeOnOverlayClick
                >
                    <div className={styles.setListModalContent}>
                        <div className={styles.setListModalHeader}>
                            <span className={styles.setListModalTitleSpacer} />
                            <span className={styles.setListModalTitle}>
                                {t('Complete the set')}
                            </span>
                            <Button
                                variant="link"
                                onClick={() => setSetListModalOpen(false)}
                                aria-label={t('Close panel')}
                                className={styles.setListModalClose}
                            >
                                <Cross width={24} height={24} />
                            </Button>
                        </div>
                        <div className={styles.setListModalScroll}>
                            {displayList.map((product, index) => (
                                <div
                                    key={`${product.id}-${index}`}
                                    className={styles.setListItemRow}
                                >
                                    <div
                                        className={
                                            styles.setListItemImageWrapper
                                        }
                                    >
                                        <input
                                            type="checkbox"
                                            className={styles.productCheckbox}
                                            checked={getProductQty(index) > 0}
                                            onChange={(e) => {
                                                e.stopPropagation()
                                                setProductQty(index, (q) =>
                                                    q > 0 ? 0 : 1
                                                )
                                            }}
                                            aria-label={t('Select product')}
                                        />
                                        <div
                                            className={styles.setListItemImage}
                                            style={{
                                                backgroundImage: `url(${product.small_image?.url})`
                                            }}
                                        />
                                    </div>
                                    <div className={styles.setListItemInfo}>
                                        <span
                                            className={styles.setListItemName}
                                        >
                                            {product.name}
                                        </span>
                                        <span
                                            className={styles.setListItemPrice}
                                        >
                                            <ProductPrice
                                                className="suggestedSetPrice"
                                                priceRange={product.price_range}
                                            />
                                        </span>
                                        {product.is_set === 1 &&
                                        typeof product.ppum === 'number' &&
                                        !isNaN(product.ppum) ? (
                                            <span
                                                className={
                                                    styles.setListItemPpum
                                                }
                                            >
                                                {t(
                                                    'common||pdp||Price per unit'
                                                )}{' '}
                                                {formatCurrency(
                                                    product.ppum ?? ''
                                                )}
                                            </span>
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Button
                            type="button"
                            className={styles.llevarSetButtonModal}
                            onClick={() => {
                                setSetListModalOpen(false)
                                addAllToCartHandler()
                            }}
                            disabled={isAdding}
                        >
                            <span style={{ textTransform: 'uppercase' }}>
                                {t('Add to cart')}
                            </span>
                        </Button>
                    </div>
                </Modal>
            )}

            {isSidebarOpen && selectedProduct && (
                <SidebarSuggestedSet
                    open={isSidebarOpen}
                    onClose={handleCloseSidebar}
                >
                    {selectedProduct.media_gallery &&
                        selectedProduct.media_gallery.length > 0 && (
                            <div className={styles.sidebarImageSlider}>
                                <Button
                                    onClick={handleCloseSidebar}
                                    variant="link"
                                    className={cn(
                                        styles['cart-btn-close'],
                                        'close-btn'
                                    )}
                                    aria-label={t('Close Panel')}
                                >
                                    <span aria-hidden="true">
                                        <Cross width={24} height={24} />
                                    </span>
                                </Button>
                                <Slider
                                    dots
                                    infinite={false}
                                    speed={300}
                                    slidesToShow={1}
                                    slidesToScroll={1}
                                    arrows
                                >
                                    {selectedProduct.media_gallery.map(
                                        (image, index) => (
                                            <div
                                                key={index}
                                                className={
                                                    styles.sliderImageWrapper
                                                }
                                            >
                                                <img
                                                    src={
                                                        image.url
                                                            ? image.url
                                                            : ''
                                                    }
                                                    alt={selectedProduct.name}
                                                    className={
                                                        styles.sliderImage
                                                    }
                                                />
                                            </div>
                                        )
                                    )}
                                </Slider>
                            </div>
                        )}
                    <div className={styles.infoContainer}>
                        <div className={styles.scrollableContent}>
                            <div className={styles.productDetails}>
                                <div className={styles.productTopInfo}>
                                    <span
                                        className={styles.sku}
                                        aria-label={selectedProduct.sku}
                                    >
                                        {t('SKU: ')}
                                        {selectedProduct.sku}
                                    </span>
                                </div>
                                <div>
                                    <h1
                                        className={cn(
                                            styles['product-name'],
                                            'heading-4'
                                        )}
                                    >
                                        {selectedProduct.name}
                                    </h1>
                                </div>
                                <div className={styles.pdpPrice}>
                                    <ProductPrice
                                        className={styles.pdpProductPrice}
                                        priceRange={selectedProduct.price_range}
                                    />
                                    {selectedProduct.is_set === 1 &&
                                    typeof selectedProduct.ppum === 'number' &&
                                    !isNaN(selectedProduct.ppum) ? (
                                        <div
                                            className={styles.pricePerUnitCont}
                                        >
                                            <span>
                                                {t(
                                                    'common||pdp||Price per unit'
                                                )}{' '}
                                                {formatCurrency(
                                                    selectedProduct.ppum
                                                        ? selectedProduct.ppum
                                                        : ''
                                                )}
                                            </span>
                                        </div>
                                    ) : null}
                                </div>

                                {selectedProduct.short_description?.html?.trim() && (
                                    <div
                                        className={
                                            styles.shortDescriptionContainer
                                        }
                                    >
                                        <h5 className="uppercase font-bold text-base md:text-l leading-5 md:leading-6 mb-2">
                                            {t('pdp||Product description')}
                                        </h5>
                                        <div
                                            className={
                                                styles['shortDescription']
                                            }
                                        >
                                            <RichContent
                                                html={
                                                    selectedProduct
                                                        .short_description.html
                                                }
                                            />
                                        </div>
                                    </div>
                                )}

                                {selectedProduct.medidas_y_materialidad && (
                                    <div
                                        className={
                                            styles.shortDescriptionContainer
                                        }
                                    >
                                        <div className="font-bold text-base md:text-l leading-5 md:leading-6 mb-2">
                                            {t(
                                                'pdp||Measurements and material'
                                            )}
                                        </div>
                                        <div
                                            className={
                                                styles['shortDescription']
                                            }
                                        >
                                            <RichContent
                                                html={
                                                    selectedProduct.medidas_y_materialidad
                                                }
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={styles.buttonsContainer}>
                            <div className={styles.qtySelector}>
                                <p className={styles.qtyTitle}>
                                    {t('pdp||QTY:')}
                                </p>
                                <QuantitySelector
                                    quantity={quantity}
                                    setQuantity={setQuantity}
                                />
                            </div>
                            <Button
                                aria-label={t('Add to Cart')}
                                type="button"
                                className={styles['add-to-cart-button']}
                                onClick={addProductHandler}
                                loading={false}
                                disabled={isButtonDisabled || isAdding}
                            >
                                {buttonLabel}
                            </Button>
                        </div>
                    </div>
                </SidebarSuggestedSet>
            )}
        </div>
    )
}

export default SuggestedSet
